import { useState, useEffect, useMemo, useRef, forwardRef, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { X, ListChecks, Plus, Sparkles, Trash2, Calendar, Check, GripVertical } from 'lucide-react'
import useSmartListStore from '../store/smartListStore'
import useAuthStore from '../store/authStore'
import { effectivePlan } from '../config/features'
import { useT } from '../i18n/useT'

/**
 * Smart List bottom sheet per un task.
 * Props:
 *  - taskId (UUID)
 *  - taskTitle (string, mostrato nell'header)
 *  - onClose ()
 *
 * Stati:
 *  - lista non caricata → loader
 *  - lista esistente → render items + add input
 *  - lista assente → empty state con CTA (manuale / AI per Premium)
 */
export default function SmartListModal({ taskId, taskTitle, onClose }) {
  const t = useT()
  const profile = useAuthStore(s => s.user)
  const isPremium = effectivePlan(profile) === 'premium'

  const cache = useSmartListStore(s => s.cache[taskId])
  const loadForTask  = useSmartListStore(s => s.loadForTask)
  const createList   = useSmartListStore(s => s.createList)
  const addItem      = useSmartListStore(s => s.addItem)
  const toggleItem   = useSmartListStore(s => s.toggleItem)
  const deleteItem   = useSmartListStore(s => s.deleteItem)
  const deleteList   = useSmartListStore(s => s.deleteList)
  const reorderItems = useSmartListStore(s => s.reorderItems)

  const [newItemText, setNewItemText] = useState('')
  const [creating, setCreating] = useState(false)
  // Drag state: { id, fromIdx, toIdx } | null. `toIdx` è l'indice di
  // INSERIMENTO nell'array di item attivi (0..activeItems.length).
  const [drag, setDrag] = useState(null)
  // Ref per ogni item — per leggere getBoundingClientRect durante il move.
  const itemRefs = useRef({})

  useEffect(() => { if (taskId) loadForTask(taskId).catch(() => {}) }, [taskId])

  const list  = cache?.list
  const items = cache?.items ?? []
  const loading = cache?.loading

  // Sorted = completati in fondo, all'interno di ciascun gruppo per `position`.
  // Splittiamo poi in `activeItems` (drag abilitato) e `completedItems`
  // (sezione finale, no drag — sposterli sopra creerebbe confusione UX visto
  // che il sort li rimanderebbe in fondo).
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1
      return a.position - b.position
    })
  }, [items])
  const activeItems    = useMemo(() => sortedItems.filter(i => !i.completed), [sortedItems])
  const completedItems = useMemo(() => sortedItems.filter(i =>  i.completed), [sortedItems])

  const doneCount = items.filter(i => i.completed).length
  const totalCount = items.length
  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0

  const handleCreateManual = async () => {
    setCreating(true)
    try { await createList(taskId, '') } catch (e) { console.error(e) }
    finally { setCreating(false) }
  }

  const handleAddItem = async (e) => {
    // preventDefault: blocca submit GET del browser. stopPropagation: impedisce
    // che il submit bubble al form parent (ManualForm) — React Portal non isola
    // gli eventi sintetici, quindi senza questo il form parent farebbe submit.
    e?.preventDefault()
    e?.stopPropagation()
    if (!newItemText.trim()) return
    const text = newItemText
    setNewItemText('')
    try { await addItem(taskId, { text }) } catch (err) { console.error(err); setNewItemText(text) }
  }

  const handleDeleteList = async () => {
    if (!confirm(t('sl_confirm_delete_list'))) return
    try { await deleteList(taskId) } catch (e) { console.error(e) }
  }

  // ── Drag & drop reorder (pointer events: funziona uguale su touch/mouse) ──
  const handleDragStart = (e, item, idx) => {
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
    setDrag({ id: item.id, fromIdx: idx, toIdx: idx })
  }

  const handleDragMove = (e) => {
    if (!drag) return
    e.preventDefault()
    const y = e.clientY
    // Determina l'indice di inserimento confrontando Y con il midpoint di
    // ogni item attivo. Se Y è sopra il midpoint dell'item i, inserisci
    // prima di i. Se sotto tutti, inserisci alla fine.
    let toIdx = activeItems.length
    for (let i = 0; i < activeItems.length; i++) {
      const ref = itemRefs.current[activeItems[i].id]
      if (!ref) continue
      const rect = ref.getBoundingClientRect()
      const mid = rect.top + rect.height / 2
      if (y < mid) { toIdx = i; break }
    }
    if (toIdx !== drag.toIdx) setDrag({ ...drag, toIdx })
  }

  const handleDragEnd = async () => {
    if (!drag) return
    const { id, fromIdx, toIdx } = drag
    setDrag(null)
    // No-op se il drop è nello stesso slot del partenza.
    if (toIdx === fromIdx || toIdx === fromIdx + 1) return
    const reordered = [...activeItems]
    const [moved] = reordered.splice(fromIdx, 1)
    // Dopo lo splice gli indici successivi a fromIdx si sono spostati di -1.
    const adjustedTo = toIdx > fromIdx ? toIdx - 1 : toIdx
    reordered.splice(adjustedTo, 0, moved)
    const allOrderedIds = [...reordered, ...completedItems].map(i => i.id)
    try { await reorderItems(taskId, allOrderedIds) } catch (err) { console.error(err) }
  }

  // ── Render ──────────────────────────────────────
  // Portal in body: il modal contiene <form> per l'add input e potrebbe essere
  // aperto da dentro un altro <form> (es. ManualForm). Form annidati sono HTML
  // illegali → React warning + comportamento submit imprevedibile.
  return createPortal(
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-sheet sl-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />

        <div className="sl-header">
          <div className="sl-header-text">
            <div className="sl-header-title">
              <ListChecks size={18} />
              <span>{list?.title || t('sl_title')}</span>
            </div>
            {taskTitle && <div className="sl-header-sub">{taskTitle}</div>}
          </div>
          <button className="btn-icon" onClick={onClose} aria-label={t('close')}>
            <X size={20} />
          </button>
        </div>

        {loading && !list && (
          <div className="sl-loading">
            <span className="sl-spinner" />
          </div>
        )}

        {!loading && !list && (
          <SmartListEmpty
            isPremium={isPremium}
            creating={creating}
            onCreateManual={handleCreateManual}
            t={t}
          />
        )}

        {list && (
          <>
            {totalCount > 0 && (
              <div className="sl-progress">
                <div className="sl-progress-text">
                  {t('sl_progress', { done: doneCount, total: totalCount })}
                </div>
                <div className="sl-progress-bar">
                  <div className="sl-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            )}

            <div className="sl-items">
              {sortedItems.length === 0 && (
                <div className="sl-items-empty">{t('sl_no_items')}</div>
              )}
              {activeItems.map((item, idx) => {
                // Drop indicator: mostra una riga colorata prima di questo item
                // se `drag.toIdx === idx` e il drop produrrebbe un cambio reale
                // di posizione (non identità: idx === fromIdx o idx === fromIdx+1
                // significherebbero "stesso posto").
                const showIndicator =
                  drag &&
                  drag.toIdx === idx &&
                  drag.fromIdx !== idx &&
                  drag.fromIdx + 1 !== idx
                return (
                  <Fragment key={item.id}>
                    {showIndicator && <div className="sl-drop-line" />}
                    <SmartListItem
                      ref={(el) => { itemRefs.current[item.id] = el }}
                      item={item}
                      onToggle={() => toggleItem(taskId, item.id)}
                      onDelete={() => deleteItem(taskId, item.id)}
                      isDragging={drag?.id === item.id}
                      dragHandleProps={{
                        onPointerDown:   (e) => handleDragStart(e, item, idx),
                        onPointerMove:   handleDragMove,
                        onPointerUp:     handleDragEnd,
                        onPointerCancel: handleDragEnd,
                        // touchAction:none impedisce al browser di intercettare
                        // il gesto come scroll quando si tiene premuto l'handle.
                        style: { touchAction: 'none' },
                        'aria-label': t('sl_drag_handle'),
                      }}
                      t={t}
                    />
                  </Fragment>
                )
              })}
              {/* Drop indicator finale: drop dopo l'ultimo active item */}
              {drag && drag.toIdx === activeItems.length && drag.fromIdx !== activeItems.length - 1 && (
                <div className="sl-drop-line" />
              )}
              {completedItems.map(item => (
                <SmartListItem
                  key={item.id}
                  item={item}
                  onToggle={() => toggleItem(taskId, item.id)}
                  onDelete={() => deleteItem(taskId, item.id)}
                  t={t}
                />
              ))}
            </div>

            <form className="sl-add" onSubmit={handleAddItem}>
              <input
                type="text"
                value={newItemText}
                onChange={e => setNewItemText(e.target.value)}
                placeholder={t('sl_add_placeholder')}
                className="sl-add-input"
              />
              <button type="submit" className="sl-add-btn" disabled={!newItemText.trim()} aria-label={t('sl_add_button')}>
                <Plus size={18} />
              </button>
            </form>

            <div className="sl-footer">
              {isPremium && (
                <button className="sl-footer-action sl-footer-action--ai" disabled title={t('sl_ai_coming_soon')}>
                  <Sparkles size={14} /> {t('sl_ai_suggest')}
                </button>
              )}
              <button className="sl-footer-action sl-footer-action--danger" onClick={handleDeleteList}>
                <Trash2 size={14} /> {t('sl_delete_list')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}

// ── Empty state ─────────────────────────────────
function SmartListEmpty({ isPremium, creating, onCreateManual, t }) {
  return (
    <div className="sl-empty">
      <div className="sl-empty-icon"><ListChecks size={36} strokeWidth={1.8} /></div>
      <h3 className="sl-empty-title">{t('sl_empty_title')}</h3>
      <p className="sl-empty-sub">{t('sl_empty_sub')}</p>
      <div className="sl-empty-actions">
        {isPremium && (
          <button className="btn btn-primary sl-empty-btn" disabled title={t('sl_ai_coming_soon')}>
            <Sparkles size={15} /> {t('sl_create_ai')}
          </button>
        )}
        <button
          className={`btn ${isPremium ? 'btn-secondary' : 'btn-primary'} sl-empty-btn`}
          onClick={onCreateManual}
          disabled={creating}
        >
          {creating ? '...' : t('sl_create_manual')}
        </button>
      </div>
    </div>
  )
}

// ── Singolo item ────────────────────────────────
const SmartListItem = forwardRef(function SmartListItem(
  { item, onToggle, onDelete, dragHandleProps, isDragging, t },
  ref,
) {
  const dueLabel = useMemo(() => {
    if (!item.dueAt) return null
    const d = new Date(item.dueAt)
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1)
    const isTomorrow = d.toDateString() === tomorrow.toDateString()
    const hh = `${d.getHours()}`.padStart(2, '0') + ':' + `${d.getMinutes()}`.padStart(2, '0')
    if (sameDay) return `${t('sl_today')} ${hh}`
    if (isTomorrow) return `${t('sl_tomorrow')} ${hh}`
    return d.toLocaleDateString() + ' ' + hh
  }, [item.dueAt])

  return (
    <div
      ref={ref}
      className={`sl-item${item.completed ? ' sl-item--done' : ''}${isDragging ? ' sl-item--dragging' : ''}`}
    >
      {dragHandleProps && (
        <button
          type="button"
          className="sl-item-drag"
          {...dragHandleProps}
        >
          <GripVertical size={16} />
        </button>
      )}
      <button className="sl-item-check" onClick={onToggle} aria-label={item.completed ? t('sl_uncheck') : t('sl_check')}>
        {item.completed && <Check size={13} strokeWidth={3} />}
      </button>
      <div className="sl-item-body">
        <div className="sl-item-text">{item.text}</div>
        {dueLabel && (
          <div className="sl-item-due">
            <Calendar size={11} /> <span>{dueLabel}</span>
          </div>
        )}
      </div>
      <button className="sl-item-delete" onClick={onDelete} aria-label={t('sl_delete_item')}>
        <Trash2 size={14} />
      </button>
    </div>
  )
})
